import React from 'react';
import Portal from './../common/Portal';
import PropTypes from 'prop-types';
import { enableRootNodeScroll } from '../util/DOMUtil';
import { KeyCode } from '../util/Constants';

import './Modal.css';
import { ThemeSetup } from '../themes/ThemeSetup';
import t from '../i18n/I18n';
import KeyboardShortcuts from '../keyboard-shortcuts/KeyboardShortcuts';
import KeydownConfig from '../keyboard-shortcuts/KeydownConfig';

const AnimationStatus = {
  ANIMATING: 1,
  ANIMATED: 2
};

export default class Modal extends React.Component {
  static propTypes = {
    onAction: PropTypes.func,
    onClose: PropTypes.func,
    closeOnEsc: PropTypes.bool,
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    name: PropTypes.string,
    actionButtonTitle: PropTypes.any,
    cancelButtonTitle: PropTypes.string,
    autoCloseOnAction: PropTypes.bool,
    noAction: PropTypes.bool,
    loading: PropTypes.bool,
    footer: PropTypes.node,
    trigger: PropTypes.object,
    zIndex: PropTypes.number,
    closeOnOutsideClick: PropTypes.bool,
    disableBackgroundScroll: PropTypes.bool
  };

  static defaultProps = {
    onAction: () => {},
    noAction: false,
    name: '',
    title: '',
    actionButtonTitle: t('DONE'),
    closeOnEsc: false,
    cancelButtonTitle: null,
    autoCloseOnAction: true,
    loading: false,
    closeOnOutsideClick: true,
    disableBackgroundScroll: false
  };

  constructor(props) {
    super(props);
    this.state = {
      animationStatus: props.trigger ? AnimationStatus.ANIMATING : AnimationStatus.ANIMATED
    };
  }
  KeyUp = (e: MouseEvent) => {
    if (e.keyCode === KeyCode.ENTER) {
      e.stopPropagation();
      this.onAction();
    }
  };
  componentDidMount() {
    const { closeOnEsc, disableBackgroundScroll, onClose, noAction } = this.props;
    if(!noAction) document.addEventListener('keyup', this.KeyUp);
    if (disableBackgroundScroll) {
      enableRootNodeScroll(false);
    }
    if (closeOnEsc) {
      this.addEventListener(onClose ? onClose : () => {});
    }
    this.setHeight();
    if (this.state.animationStatus === AnimationStatus.ANIMATING) {
      const requestAnimationFrame = window.requestAnimationFrame || window.setTimeout;
      requestAnimationFrame(() => this.setState({ animationStatus: AnimationStatus.ANIMATED }));
    }

    if (!this._node) {
      return;
    }

    const input = this._node.querySelector('input[type="text"]') || this._node.querySelector('input');
    if (input && input.focus) {
      input.focus();
    }
  }

  addEventListener = (onClose) => {
    KeyboardShortcuts.add(new KeydownConfig('MODAL_1', onClose, [KeyCode.ESCAPE]));
  };

  removeEventListener = () => {
    KeyboardShortcuts.remove('MODAL_1');
  };

  componentDidUpdate() {
    this.setHeight();
  }

  componentWillUnmount() {
    if (this.props.disableBackgroundScroll) {
      enableRootNodeScroll(true);
    }
    this.removeEventListener();
    if(!this.props.noAction) document.removeEventListener('keyup', this.KeyUp);
  }

  KeyUp = (e: MouseEvent) => {
    if (e.keyCode === KeyCode.ENTER) {
      e.stopPropagation();
      this.onAction();
    }
  };

  setHeight = () => {
    if (this._contentNode) {
      this._contentNode.style.maxHeight = `${window.innerHeight - 200}px`;
    }
  };

  render() {
    const { loading, name, footer, onClose } = this.props;
    const modalBackGroundStyle = {};
    if (this.props.zIndex) {
      modalBackGroundStyle.zIndex = this.props.zIndex;
    }

    return (
      <Portal defaultOpen={true} closeOnEsc onClose={onClose} closeOnOutsideClick>
        <div
          ref={(node) => (this._node = node)}
          onClick={(event) => this.onClick(event)}
          className="modal-background"
          name={name}
          style={modalBackGroundStyle}>
          <div className="modal">
            <div className="modal-content">
              {this.props.title && <div className="modal-header">{this.props.title}</div>}
              {loading ? (
                <div className="flex-row align-center justify-center" style={{ height: 100 }}>
                  <img className="loading-icon" src={ThemeSetup.getImageURL('RingSpinner.svg')} />
                </div>
              ) : (
                <div className="modal-content-inner" ref={(node) => (this._contentNode = node)}>
                  {this.props.children}
                </div>
              )}
            </div>
            {footer ? (
              footer
            ) : this.props.cancelButtonTitle || this.props.actionButtonTitle ? (
              <div className="modal-footer">
                <span className={`modal-footer-right ${loading ? 'disabled' : ''}`} onClick={this.onAction}>
                  {this.props.actionButtonTitle}
                </span>
                {this.props.cancelButtonTitle && (
                  <div
                    className={`modal-footer-left ${loading ? 'disabled' : ''}`}
                    onClick={() => this.props.onClose()}>
                    {this.props.cancelButtonTitle}
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>
      </Portal>
    );
  }

  onClick(event) {
    if (event.target == event.currentTarget && this.props.closeOnOutsideClick) {
      if (this.props.onClose) {
        this.props.onClose();
      }
      event.stopPropagation();
    }
  }

  onAction = () => {
    const { onAction, onClose, autoCloseOnAction } = this.props;
    onAction();
    if (autoCloseOnAction) {
      onClose();
    }
  };
}
